import React from 'react';

const MAX_LINES = 12;

const PILL_CANCEL =
  'inline-flex h-8 items-center justify-center rounded-full bg-white/15 px-3.5 text-sm font-medium text-white hover:bg-white/25 disabled:opacity-40 transition-colors';

const PILL_SEND =
  'inline-flex h-8 items-center justify-center rounded-full bg-white px-3.5 text-sm font-semibold text-pipedrive-blue hover:bg-blue-50 disabled:opacity-40 transition-colors';

export function UserChatEditBubble({
  value,
  onChange,
  onCancel,
  onSubmit,
  submitDisabled,
  children,
}: {
  value: string;
  onChange: (next: string) => void;
  onCancel: () => void;
  onSubmit: () => void;
  submitDisabled?: boolean;
  children?: React.ReactNode;
}) {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);

  const syncHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight);
    const padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 20;
    const maxH = lh * MAX_LINES + (Number.isFinite(padY) ? padY : 0);
    const next = Math.min(el.scrollHeight, maxH);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
  }, []);

  React.useLayoutEffect(() => {
    syncHeight();
  }, [value, syncHeight]);

  React.useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, []);

  return (
    <div className="w-full min-w-0 max-w-[min(42rem,85%)]">
      <div className="rounded-2xl rounded-tr-md bg-pipedrive-blue px-3.5 pt-2.5 pb-3 text-white">
        {children}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (!submitDisabled) onSubmit();
            }
          }}
          rows={1}
          aria-label="Nachricht bearbeiten"
          className="w-full resize-none bg-transparent border-0 p-0 outline-none text-sm leading-5 text-white caret-white placeholder:text-white/50 selection:bg-white/30"
        />
        <div className="mt-2.5 flex justify-end gap-2">
          <button type="button" className={PILL_CANCEL} onClick={onCancel}>
            Abbrechen
          </button>
          <button type="button" className={PILL_SEND} disabled={submitDisabled} onClick={onSubmit}>
            Senden
          </button>
        </div>
      </div>
    </div>
  );
}
